/**
 * Spinner — the one rotating loader in the system.
 *
 * Motion-safe by construction: the reduced-motion gate lives here, not at the call site.
 *
 *   <Spinner />              // 13px, inherits currentColor
 *   <Spinner size={16} />
 *   <Spinner className="text-aq-ink-muted" />
 *
 * `currentColor` on both strokes, so it takes the colour of whatever it sits
 * in and needs no variant matrix. `aria-hidden` because a spinner is never the
 * accessible name for anything: the surrounding control owns `aria-busy` and
 * the label.
 */
import type { ReactElement } from 'react';

export type SpinnerProps = {
  /** Diameter in px. Defaults to 13, the `md` button icon size. */
  size?: number;
  /** Extra classes, typically a colour token. */
  className?: string;
};

export function Spinner({ size = 13, className }: SpinnerProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={['shrink-0 motion-safe:animate-spin', className ?? ''].join(' ')}
    >
      {/* Track at low opacity, then a quarter arc as the moving part. Reads as
          rotation rather than as a pulsing ring, and holds up at 11px. */}
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
